import { useState } from "react";
import { KeyRound, Mail, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { validate } from "../../lib/validate";
import { Field, inputProps } from "../site/Primitives";

const useForm = (initial) => {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ busy: false, done: false, error: null });
  const set = (k) => (e) => { setValues((v) => ({ ...v, [k]: e.target.value })); setErrors((er) => ({ ...er, [k]: undefined })); };
  return { values, setValues, errors, setErrors, state, setState, set };
};

const Status = ({ state, doneText, testid }) => (
  <>
    {state.done && <p className="success-msg" role="status" data-testid={`${testid}-success`}><CheckCircle2 aria-hidden="true" />{doneText}</p>}
    {state.error && <p className="error-banner" role="alert" data-testid={`${testid}-error`}><AlertCircle aria-hidden="true" />{state.error}</p>}
  </>
);

export const ChangePassword = () => {
  const { t } = useLang();
  const a = t.account;
  const { user, signIn } = useAuth();
  const f = useForm({ current: "", next: "", confirm: "" });

  if (!user.providers?.includes("password")) return <p className="f-note" data-testid="change-password-google-only">{a.googleOnly}</p>;

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f.values, { current: ["required"], next: ["required", ["min", 8]], confirm: ["required"] });
    if (!errs.confirm && f.values.confirm !== f.values.next) errs.confirm = ["mismatch"];
    f.setErrors(errs);
    if (Object.keys(errs).length) return;
    f.setState({ busy: true, done: false, error: null });
    try {
      const { data } = await api.post("/auth/change-password", { current_password: f.values.current, new_password: f.values.next });
      signIn(data);
      f.setValues({ current: "", next: "", confirm: "" });
      f.setState({ busy: false, done: true, error: null });
    } catch (err) {
      f.setState({ busy: false, done: false, error: err?.response?.status === 400 ? a.wrongCurrent : t.errors.generic });
    }
  };

  const inp = (k, id) => ({ type: "password", ...inputProps(id, f.errors[k], true), value: f.values[k], onChange: f.set(k) });
  return (
    <form onSubmit={submit} noValidate data-testid="change-password-form">
      <h3 className="sub-title" style={{ marginBottom: 16 }}>{a.changePassword}</h3>
      <div className="f-grid">
        <Field id="cp-current" label={a.current} error={f.errors.current} required><input autoComplete="current-password" {...inp("current", "cp-current")} /></Field>
        <div className="f-field" />
        <Field id="cp-new" label={a.newPassword} error={f.errors.next} required><input autoComplete="new-password" {...inp("next", "cp-new")} /></Field>
        <Field id="cp-confirm" label={a.confirm} error={f.errors.confirm} required><input autoComplete="new-password" {...inp("confirm", "cp-confirm")} /></Field>
      </div>
      <div className="f-actions"><span />
        <button type="submit" className="btn btn-primary" disabled={f.state.busy} data-testid="change-password-submit-btn">
          {f.state.busy ? <Loader2 className="spin" aria-hidden="true" /> : <KeyRound aria-hidden="true" />}{a.savePassword}
        </button>
      </div>
      <Status state={f.state} doneText={a.passwordDone} testid="change-password" />
    </form>
  );
};

export const ChangeAdminEmail = () => {
  const { t } = useLang();
  const a = t.account;
  const { user, signIn } = useAuth();
  const f = useForm({ email: user.email, current: "" });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f.values, { email: ["required", "email"], current: ["required"] });
    f.setErrors(errs);
    if (Object.keys(errs).length) return;
    f.setState({ busy: true, done: false, error: null });
    try {
      const { data } = await api.put("/admin/account/email", { email: f.values.email, current_password: f.values.current });
      signIn(data);
      f.setValues({ email: data.user.email, current: "" });
      f.setState({ busy: false, done: true, error: null });
    } catch (err) {
      const s = err?.response?.status;
      f.setState({ busy: false, done: false, error: s === 400 ? a.wrongCurrent : s === 409 ? a.emailTaken : t.errors.generic });
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="change-email-form">
      <h3 className="sub-title" style={{ marginBottom: 16 }}>{a.changeEmail}</h3>
      <div className="f-grid">
        <Field id="ce-email" label={a.newEmail} error={f.errors.email} required>
          <input type="email" autoComplete="email" {...inputProps("ce-email", f.errors.email, true)} value={f.values.email} onChange={f.set("email")} />
        </Field>
        <Field id="ce-current" label={a.current} error={f.errors.current} required>
          <input type="password" autoComplete="current-password" {...inputProps("ce-current", f.errors.current, true)} value={f.values.current} onChange={f.set("current")} />
        </Field>
      </div>
      <div className="f-actions"><span />
        <button type="submit" className="btn btn-primary" disabled={f.state.busy} data-testid="change-email-submit-btn">
          {f.state.busy ? <Loader2 className="spin" aria-hidden="true" /> : <Mail aria-hidden="true" />}{a.saveEmail}
        </button>
      </div>
      <Status state={f.state} doneText={a.emailDone} testid="change-email" />
    </form>
  );
};
